import {
  FilePurpose,
  languageDisplayCode,
  languageDisplayLabel,
  LanguagesEnum,
} from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  CourseContentType,
  CourseFormState,
  ModifiedResource,
  ResourceType,
} from '@/client/courses';
import { File } from '@/client/files';
import { LoadingStatuses } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { VideoFallbackMessage } from '@/components/courses/VideoFallbackMessage';
import { useToast } from '@/hooks/useToast';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { ContentFileUpload } from '@/ui/content-file-upload';
import { EditableText } from '@/ui/editable-text';
import { LanguageTabs } from '@/ui/language-tabs';
import { FlexContainer } from '@/ui/styled-ui';
import {
  convertFilesToArrayFormat,
  getFullyTranslatedLanguagesForFiles,
  GlobeIcon,
  modifyCourseResource,
  PencilIcon,
  sortLanguagesWithPriority,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { Dialog, DialogProps } from 'primereact/dialog';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 650px;
  max-height: 520px;

  &.v2 {
    .actions {
      margin-top: var(--semi-big-padding);
    }

    .p-dialog-title h1 {
      font-weight: 600;
      font-size: var(--heading-small-font-size);
    }
  }
`;

const StyledPanel = styled(Panel)`
  position: relative;
  margin-top: var(--default-padding);

  .p-panel-header,
  .p-panel-content {
    padding: 0;
    border: 0;
  }
`;

const StyledFlexContainer = styled(FlexContainer)`
  position: relative;
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  margin-top: calc(var(--small-padding) / 2);
`;

type ContentResourceModalProps = {
  index?: number;
  values: CourseFormState;
  visible: boolean;
  isDisabled: boolean;
  onHide: () => void;
  setFieldValue: (field: string, value: any) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
};

export const ContentResourceModal: React.FC<
  ContentResourceModalProps & DialogProps
> = ({
  index = 0,
  values,
  visible,
  isDisabled,
  setFieldValue,
  setIsSubmitDisabled,
  onHide,
}) => {
  const { id: courseId } = useParams();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTabsDisabled, setIsTabsDisabled] = useState<boolean>(false);

  const [selectedFileTab, setSelectedFileTab] = useState<
    keyof typeof LanguagesEnum
  >(languageDisplayCode[LanguagesEnum.EN] as keyof typeof LanguagesEnum);
  const [resource, setResource] = useState<ModifiedResource | null>();
  const [tempResource, setTempResource] = useState<ModifiedResource | null>();

  const [requiredTranslations, setRequiredTranslations] = useState([
    languageDisplayCode[LanguagesEnum.EN],
  ]);

  useEffect(() => {
    const fetchResource = async () => {
      const resourceId = values.courseContents?.[index]?.resource?.id;
      const shouldFetch = !!resourceId && !resourceId?.includes('temp');

      if (courseId && shouldFetch) {
        setIsLoading(true);
        try {
          const resourceData = await client.courses.getCourseResource(
            courseId,
            resourceId || '',
          );

          if (resourceData) {
            const modifiedResource = modifyCourseResource(resourceData);

            if (modifiedResource) {
              setResource(modifiedResource);
              setTempResource(JSON.parse(JSON.stringify(modifiedResource)));
              setItemName(modifiedResource.title || generateItemTitle());
            }
          }
        } catch (e) {
          handleAxiosError(e as AxiosError, toast);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (visible) {
      setItemName(
        values.courseContents[index]?.resource?.title || generateItemTitle(),
      );

      fetchResource();
    }
  }, [visible, courseId, index]);

  useEffect(() => {
    if (values?.courseLanguages) {
      setRequiredTranslations(
        values.courseLanguages.map(
          (languageCode: string) => languageDisplayCode[languageCode],
        ),
      );
    }

    const tab = values.courseLanguages?.length
      ? sortLanguagesWithPriority(
          values.courseLanguages?.map(
            (languageCode: string) => languageDisplayCode[languageCode],
          ),
        )[0]
      : languageDisplayCode[LanguagesEnum.EN];
    setSelectedFileTab(tab as keyof typeof LanguagesEnum);
  }, [values.courseLanguages]);

  useEffect(() => {
    setIsSubmitDisabled(isTabsDisabled);
  }, [isTabsDisabled]);

  const generateItemTitle = () => {
    const itemType = values?.courseContents[index]?.resource?.type;

    switch (itemType) {
      case ResourceType.SCORM:
        return t('content.name.your.item', {
          contentItem: t('content.scorm'),
        });
      case ResourceType.PDF:
        return t('content.name.your.item', { contentItem: t('content.pdf') });
      case ResourceType.VIDEO:
        return t('content.name.your.item', {
          contentItem: t('content.video'),
        });
      default:
        return '';
    }
  };

  const [itemName, setItemName] = useState<string>(
    values.courseContents[index]?.resource?.title || generateItemTitle(),
  );

  const headerTemplate = () => (
    <FlexContainer wrap="wrap">
      {!isLoading && (
        <h1 className="flex-1 w-full">
          <EditableText
            key={itemName}
            value={itemName}
            icon={<PencilIcon />}
            saveValue={(value: string) => {
              if (value !== itemName) {
                setFieldValue(`courseContents.${index}.isAutoNamed`, false);
              }
              setItemName(value);
            }}
          />
        </h1>
      )}
    </FlexContainer>
  );

  const handleFileUpload = (index: number, file: File | null) => {
    const selectedLanguage = LanguagesEnum[selectedFileTab];
    const currentTranslations = resource?.translations || [];

    const updatedFile = {
      id: resource?.id || '',
      title: resource?.title || '',
      type: (resource?.type ||
        values.courseContents[index]?.type) as ResourceType,
      translations: {
        ...currentTranslations,
        [selectedLanguage]: {
          file,
        },
      },
    } as ModifiedResource;

    setResource(updatedFile);
  };

  const handleSaveResource = async () => {
    if (!courseId) return;

    setIsSubmitting(true);
    try {
      const resourceData = {
        id: values.courseContents[index]?.resource?.id?.includes('temp')
          ? undefined
          : values.courseContents[index]?.resource?.id,
        title: itemName,
        type: (resource?.type ||
          values.courseContents[index]?.type) as ResourceType,
        position: index,
        translations: resource
          ? convertFilesToArrayFormat(resource)?.translations || []
          : [],
      };

      let response;
      const resourceId = resourceData.id;
      if (resourceId) {
        response = await client.courses.updateCourseResource(
          courseId,
          resourceId,
          resourceData,
        );
      } else {
        response = await client.courses.createCourseResource(
          courseId,
          resourceData,
        );
      }

      const modifiedResource = modifyCourseResource(response);

      if (modifiedResource) {
        const updatedContents = [...values.courseContents];
        updatedContents[index] = {
          ...updatedContents[index],
          resource: modifiedResource,
          type: CourseContentType.RESOURCE,
          position: index,
        };
        setFieldValue('courseContents', updatedContents);
        onHide();
      }
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validLanguages = getFullyTranslatedLanguagesForFiles(
    values?.courseLanguages || [],
    resource,
  );

  const isVideoResource =
    values?.courseContents[index]?.resource?.type === ResourceType.VIDEO;
  const fallbackLanguages =
    isVideoResource && validLanguages.length > 0
      ? requiredTranslations.filter((lang) => !validLanguages.includes(lang))
      : [];
  const videoResource = isVideoResource
    ? values?.courseContents[index]?.resource
    : undefined;
  const fallbackLanguageLabel =
    languageDisplayLabel[videoResource?.fallbackLanguage ?? LanguagesEnum.EN] ??
    '';
  const isSelectedFallback = fallbackLanguages.includes(
    selectedFileTab as string,
  );

  return (
    <>
      <StyledDialog
        blockScroll
        visible={visible}
        onHide={onHide}
        header={headerTemplate}
        draggable={false}
        className="v2"
        data-testid="content-resource-modal"
      >
        {isLoading ? (
          <FlexContainer justify="center" align="center" className="h-full">
            <ProgressSpinner />
          </FlexContainer>
        ) : (
          <>
            <FlexContainer justify="flex-start" direction="column" gap={16}>
              <FlexContainer justify="flex-start" gap={8}>
                <GlobeIcon />
                {t('content.chosen.languages')}
              </FlexContainer>
              <LanguageTabs
                options={values?.courseLanguages || []}
                requiredOptions={requiredTranslations}
                validOptions={validLanguages}
                fallbackOptions={fallbackLanguages}
                isDisabled={isDisabled || isTabsDisabled}
                onToggle={(option) =>
                  setSelectedFileTab(option as keyof typeof LanguagesEnum)
                }
                defaultSelected={selectedFileTab}
              />
            </FlexContainer>

            {isSelectedFallback && (
              <VideoFallbackMessage
                severity="info"
                className="mt-4"
                text={t('course.video.fallback.resourceModal.v2', {
                  language:
                    languageDisplayLabel[
                      LanguagesEnum[
                        selectedFileTab as keyof typeof LanguagesEnum
                      ]
                    ],
                  fallbackLanguage: fallbackLanguageLabel,
                })}
              />
            )}

            <StyledPanel>
              <StyledFlexContainer justify="flex-start" direction="column">
                <div className="field w-full mt-0 mb-0">
                  <ContentFileUpload
                    key={selectedFileTab}
                    selectedLanguage={selectedFileTab}
                    preselectedFile={
                      resource?.translations[LanguagesEnum[selectedFileTab]]
                        ?.file
                    }
                    purpose={
                      values?.courseContents[index]?.resource?.type ===
                      ResourceType.SCORM
                        ? FilePurpose.COURSE
                        : FilePurpose.MATERIAL
                    }
                    fileType={values?.courseContents[index]?.resource?.type}
                    isDisabled={isDisabled}
                    setIsTabsDisabled={setIsTabsDisabled}
                    onFileUpload={(file: File | null) =>
                      handleFileUpload(index, file)
                    }
                  />
                </div>
              </StyledFlexContainer>
            </StyledPanel>

            <FlexContainer justify="flex-start" className="actions">
              <>
                <AppButton
                  label={t('button.cancel')}
                  type="outlined"
                  className="mr-3"
                  isDisabled={isSubmitting}
                  onClick={() => {
                    if (
                      JSON.stringify(resource) !== JSON.stringify(tempResource)
                    ) {
                      setDialogData({
                        type: DialogType.CONFIRMATION,
                        show: true,
                        header: t('dialog.unsavedChanges'),
                        message: t('dialog.unsavedChanges.confirm'),
                        onAccept: () => onHide(),
                      });
                    } else {
                      onHide();
                    }
                  }}
                />
                <AppButton
                  state={
                    isSubmitting
                      ? LoadingStatuses.LOADING
                      : LoadingStatuses.IDLE
                  }
                  label={t('button.save')}
                  onClick={handleSaveResource}
                  isDisabled={isSubmitting}
                />
              </>
            </FlexContainer>
          </>
        )}
      </StyledDialog>
    </>
  );
};
